import { useCallback, useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import AdminInvoicesSection from "@/components/AdminInvoicesSection";
import { ContactActivity } from "@/components/ContactDetailSheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import OrderDetailSheet from "@/components/OrderDetailSheet";
import {
  getProfile,
  adminUpdateUser,
  adminSetPassword,
  fetchAdminRoles,
  fetchMyOrders,
  fetchAdminUsers,
  checkUsernameAvailable,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  KeyRound,
  Mail,
  Phone,
  BadgePercent,
  ShieldCheck,
  CircleDot,
  ShoppingBag,
  ShoppingCart,  ChevronDown,
  Ticket,
  Package,
  AtSign,
  Check,
  X,
  MapPin,
  Truck,
  UserRoundCheck,
  Search,
  ChevronRight,
} from "lucide-react";

const FALLBACK_ROLES = [
  "customer",
  "ambassador",
  "spa",
  "professional",
  "administrator",
];

export default function AdminUserEditSheet({
  open,
  onOpenChange,
  callerUserRecordId,
  userRecordId,
  pendingCartItems = [],
  hasPendingCart = false,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    approved: 0,
    commission_perc: 0,
    tax_exempted: 0,
    role_id: "",
    user_name: "",
  });
  const [roles, setRoles] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  // Ambassador (upline dealer) picker.
  const [ambSel, setAmbSel] = useState(null); // { id, name, email }
  const [ambInitialId, setAmbInitialId] = useState("");
  const [ambQuery, setAmbQuery] = useState("");
  const [ambResults, setAmbResults] = useState([]);
  const [ambSearching, setAmbSearching] = useState(false);
  // Expand a recent order into the full OrderDetailSheet.
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderSheetOpen, setOrderSheetOpen] = useState(false);
  // Recent orders + collapse state.  Both sections default to expanded
  // so the admin sees activity immediately on click.
  const [recentOrders, setRecentOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(true);
  const [cartOpen, setCartOpen] = useState(true);

  const loadUser = useCallback(async () => {
    if (!userRecordId) return;
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        getProfile(userRecordId).catch(() => null),
        fetchAdminRoles(callerUserRecordId).catch(() => null),
      ]);
      if (p?.profile) {
        setProfile(p.profile);
        setAmbInitialId(p.profile.ambassador_id || "");
        setAmbSel(
          p.profile.ambassador_id
            ? {
                id: p.profile.ambassador_id,
                name: p.profile.ambassador_name || p.profile.ambassador_email || "Assigned dealer",
                email: p.profile.ambassador_email || "",
              }
            : null
        );
        setAmbQuery("");
        setAmbResults([]);
        setForm({
          approved: p.profile.approved || 0,
          commission_perc: p.profile.commission_perc || 0,
          tax_exempted: p.profile.tax_exempted || 0,
          role_id: "",
          user_name: p.profile.user_name || "",
          first_name: p.profile.first_name || "",
          last_name: p.profile.last_name || "",
          phone_no: p.profile.phone_no || "",
          // Billing address
          address: p.profile.address || "",
          address2: p.profile.address2 || "",
          city: p.profile.city || "",
          state: p.profile.state || "",
          postcode: p.profile.postcode || "",
          country: p.profile.country || "US",
          // Shipping address
          ship_same_as_billing: p.profile.ship_same_as_billing || 0,
          ship_address: p.profile.ship_address || "",
          ship_address2: p.profile.ship_address2 || "",
          ship_city: p.profile.ship_city || "",
          ship_state: p.profile.ship_state || "",
          ship_postcode: p.profile.ship_postcode || "",
          ship_country: p.profile.ship_country || "US",
        });
      }
      if (r?.roles) setRoles(r.roles);
    } finally {
      setLoading(false);
    }
  }, [userRecordId, callerUserRecordId]);

  useEffect(() => {
    if (!(open && userRecordId)) return;
    let active = true;
    (async () => {
      try {
        if (!active) return;
        await loadUser();
      } catch {
        /* swallow */
      }
    })();
    return () => {
      active = false;
    };
  }, [open, userRecordId, loadUser]);

  // Debounced dealer search for the Ambassador picker.
  useEffect(() => {
    const q = ambQuery.trim();
    if (!open || q.length < 2) {
      setAmbResults([]);
      return;
    }
    let active = true;
    setAmbSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetchAdminUsers({
          callerUserRecordId,
          q,
          limit: 8,
          sort: "recent_orders",
        });
        if (!active) return;
        setAmbResults((res?.users || []).filter((u) => u.id !== userRecordId));
      } catch {
        if (active) setAmbResults([]);
      } finally {
        if (active) setAmbSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [ambQuery, open, callerUserRecordId, userRecordId]);

  // Pull the user's recent orders the moment we know their numeric user_id.
  // The backend caches /postcue/my-orders/{user_id} for 60s so this is
  // essentially free on repeat clicks.
  /* eslint-disable */
  useEffect(() => {
    if (!open || !profile?.user_id) return;
    let active = true;
    setLoadingOrders(true);
    (async () => {
      try {
        const res = await fetchMyOrders(profile.user_id, 25);
        if (!active) return;
        setRecentOrders(res?.orders || []);
      } catch {
        if (active) setRecentOrders([]);
      } finally {
        if (active) setLoadingOrders(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, profile?.user_id]);
  /* eslint-enable */

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    if (!userRecordId) return;
    setSaving(true);
    try {
      const payload = {
        caller_user_record_id: callerUserRecordId,
        user_record_id: userRecordId,
        approved: Number(form.approved) || 0,
        commission_perc: Number(form.commission_perc) || 0,
        tax_exempted: Number(form.tax_exempted) || 0,
        // Contact
        first_name: (form.first_name || "").trim(),
        last_name: (form.last_name || "").trim(),
        phone_no: (form.phone_no || "").trim(),
        // Billing address
        address: (form.address || "").trim(),
        address2: (form.address2 || "").trim(),
        city: (form.city || "").trim(),
        state: (form.state || "").trim(),
        postcode: (form.postcode || "").trim(),
        country: (form.country || "").trim(),
        // Shipping address
        ship_same_as_billing: Number(form.ship_same_as_billing) || 0,
        ship_address: (form.ship_address || "").trim(),
        ship_address2: (form.ship_address2 || "").trim(),
        ship_city: (form.ship_city || "").trim(),
        ship_state: (form.ship_state || "").trim(),
        ship_postcode: (form.ship_postcode || "").trim(),
        ship_country: (form.ship_country || "").trim(),
      };
      if (form.role_id) payload.role_id = form.role_id;
      // Ambassador (upline dealer) — only send when it actually changed, so we
      // don't trigger a needless retro-attach of orders on every save.
      const newAmbId = ambSel?.id || "";
      if (newAmbId !== ambInitialId) {
        payload.ambassador_id = newAmbId; // "" clears the ambassador
      }
      // Only send user_name if it actually changed — avoids the
      // availability check tripping on the user's existing handle.
      const cleanHandle = (form.user_name || "").trim();
      if (cleanHandle && cleanHandle !== (profile?.user_name || "")) {
        payload.user_name = cleanHandle;
      }
      const res = await adminUpdateUser(payload);
      if (res?.profile) {
        setProfile(res.profile);
        setAmbInitialId(res.profile.ambassador_id || "");
      }
      const reattached = res?.orders_reattached || 0;
      toast.success(
        reattached > 0
          ? `User updated · ${reattached} order${reattached === 1 ? "" : "s"} re-attached to dealer`
          : "User updated"
      );
      onSaved?.(res?.profile);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Could not update user"
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword() {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPwd(true);
    try {
      await adminSetPassword(callerUserRecordId, userRecordId, newPassword);
      toast.success("Password reset");
      setNewPassword("");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Could not reset password"
      );
    } finally {
      setSavingPwd(false);
    }
  }

  const name = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
      profile.user_name ||
      profile.email_id
    : "User";

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0 [&>button]:hidden"
        data-testid="admin-edit-sheet"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <SheetHeader className="text-left space-y-1">
              <SheetTitle className="text-lg font-semibold tracking-tight text-zinc-900">
                {loading ? "Loading…" : name}
              </SheetTitle>
              <SheetDescription className="text-xs text-zinc-500">
                {profile?.email_id || ""}
              </SheetDescription>
            </SheetHeader>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close and return to CRM"
              data-testid="admin-edit-close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading || !profile ? (
          <div className="p-10 flex flex-col items-center text-zinc-500">
            <Loader2 className="animate-spin mb-2" />
            <span className="text-sm">Loading user…</span>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-5 pb-32">
            {/* Identity card */}
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50/50 p-4 space-y-2">
              <div className="flex items-center gap-3">
                {profile.image_url ? (
                  <img
                    src={profile.image_url}
                    alt={name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-base font-semibold">
                    {(profile.first_name?.[0] || "?").toUpperCase()}
                    {(profile.last_name?.[0] || "").toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-zinc-900 truncate">
                    {name}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {profile.role_name ? (
                      <Badge
                        variant="secondary"
                        className="capitalize bg-violet-100 text-violet-700 border-0"
                      >
                        {profile.role_name}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-sm text-zinc-700 pt-2">
                {profile.email_id && (
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-zinc-400" />
                    <span className="truncate">{profile.email_id}</span>
                  </div>
                )}
                {profile.phone_no && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-zinc-400" />
                    <span>{profile.phone_no}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Pending cart (collapsible) — appears whenever the user has
                a Cart-full=True flag in Airtable, even if no Cart rows
                could be enriched with line items. */}
            {(hasPendingCart || pendingCartItems.length > 0) && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/40 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCartOpen((v) => !v)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left"
                  data-testid="admin-edit-cart-toggle"
                >
                  <ShoppingCart size={15} className="text-amber-600" />
                  <span className="flex-1 text-sm font-semibold text-zinc-900">
                    Pending cart
                  </span>
                  <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                    {pendingCartItems.length || "•"}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-zinc-400 transition-transform ${
                      cartOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {cartOpen && (
                  <>
                    {pendingCartItems.length > 0 ? (
                      <ul
                        className="divide-y divide-amber-100 border-t border-amber-100 bg-white"
                        data-testid="admin-edit-cart-items"
                      >
                        {pendingCartItems.map((it) => (
                          <li
                            key={it.cart_record_id}
                            className="flex items-center gap-2.5 px-4 py-2.5"
                          >
                            {it.image_url ? (
                              <img
                                src={it.image_url}
                                alt={it.product_name || "product"}
                                className="w-10 h-10 rounded-lg object-cover bg-zinc-100 shrink-0"
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                                <Package size={14} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-zinc-900 truncate">
                                {it.product_name || "Product"}
                              </div>
                              <div className="text-[11px] text-zinc-500">
                                Qty {it.qty}
                                {it.days_in_cart
                                  ? ` · ${it.days_in_cart}d in cart`
                                  : ""}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-zinc-900 shrink-0 text-right">
                              ${Number(it.price || 0).toFixed(2)}
                              {Number(it.msrp || 0) > Number(it.price || 0) && (
                                <div className="text-[10px] text-zinc-400 line-through font-normal">
                                  ${Number(it.msrp).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                        {(() => {
                          const sub = pendingCartItems.reduce(
                            (s, it) => s + Number(it.msrp || it.price || 0) * Number(it.qty || 1), 0);
                          const tot = pendingCartItems.reduce(
                            (s, it) => s + Number(it.price || 0) * Number(it.qty || 1), 0);
                          const code = pendingCartItems.find((it) => it.coupon_code)?.coupon_code || "";
                          const discount = sub - tot;
                          return (
                            <li className="px-4 py-3 bg-amber-50/60 space-y-1" data-testid="admin-edit-cart-totals">
                              <div className="flex justify-between text-xs text-zinc-600">
                                <span>Subtotal</span><span>${sub.toFixed(2)}</span>
                              </div>
                              {discount > 0.005 && (
                                <div className="flex justify-between text-xs text-emerald-700">
                                  <span className="flex items-center gap-1">
                                    <Ticket size={12} />
                                    {code ? (
                                      <span className="font-mono font-semibold">{code}</span>
                                    ) : (
                                      <span>Discount</span>
                                    )}
                                  </span>
                                  <span className="font-semibold">−${discount.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-sm font-bold text-zinc-900 pt-0.5 border-t border-amber-200">
                                <span>Total</span>
                                <span data-testid="admin-edit-cart-total">${tot.toFixed(2)}</span>
                              </div>
                            </li>
                          );
                        })()}
                      </ul>
                    ) : (
                      <div
                        className="border-t border-amber-100 bg-white px-4 py-3 text-xs text-zinc-500"
                        data-testid="admin-edit-cart-empty"
                      >
                        This user has the <b>Cart-full</b> flag in Airtable
                        but their Cart rows aren&apos;t linked back. Open
                        Airtable → Cart and re-link if you need details.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Recent orders (collapsible) */}
            <div className="rounded-2xl border border-zinc-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setOrdersOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left"
                data-testid="admin-edit-orders-toggle"
              >
                <ShoppingBag size={15} className="text-emerald-600" />
                <span className="flex-1 text-sm font-semibold text-zinc-900">
                  Recent orders
                </span>
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                  {loadingOrders ? "…" : recentOrders.length}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-zinc-400 transition-transform ${
                    ordersOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {ordersOpen && (
                <div className="border-t border-zinc-100 bg-white">
                  {loadingOrders ? (
                    <div className="py-6 text-center text-zinc-500 text-xs">
                      <Loader2
                        size={13}
                        className="animate-spin inline mr-1"
                      />
                      Loading orders…
                    </div>
                  ) : recentOrders.length === 0 ? (
                    <div className="py-6 text-center text-zinc-500 text-xs">
                      No orders yet.
                    </div>
                  ) : (
                    <ul
                      className="divide-y divide-zinc-100 max-h-[260px] overflow-y-auto"
                      data-testid="admin-edit-orders-list"
                    >
                      {recentOrders.map((o) => (
                        <li key={o.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setExpandedOrderId(o.id);
                              setOrderSheetOpen(true);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-violet-50/60 transition-colors"
                            data-testid={`admin-edit-order-${o.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-zinc-900 truncate">
                                #{o.order_id || o.post_id || o.id?.slice(-6)?.toUpperCase()}
                                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-zinc-400">
                                  {o.status || o.post_status || ""}
                                </span>
                              </div>
                              <div className="text-[11px] text-zinc-500">
                                {(o.date || o.post_date_time)
                                  ? new Date(
                                      o.date || o.post_date_time
                                    ).toLocaleDateString("en-US")
                                  : ""}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-zinc-900 shrink-0">
                              ${Number(o.total || 0).toFixed(2)}
                            </div>
                            <ChevronRight size={15} className="text-zinc-400 shrink-0" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Invoices (dealer sales made outside the web shop) */}
            <AdminInvoicesSection
              userRecordId={userRecordId}
              callerUserRecordId={callerUserRecordId}
              open={open}
            />

            {/* Communication log: emails opened + chat/SMS */}
            <ContactActivity contactRecordId={userRecordId} callerUserRecordId={callerUserRecordId} />

            {/* Admin controls */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Admin controls
              </h3>

              {/* Shop handle (user_name) — admin can rename a dealer's
                  public shop URL with live availability check. */}
              <AdminShopHandleField
                value={form.user_name || ""}
                onChange={(v) => update("user_name", v)}
                userRecordId={userRecordId}
              />

              {/* Role */}
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select
                  value={form.role_id}
                  onValueChange={(v) => update("role_id", v)}
                >
                  <SelectTrigger
                    className="h-11 rounded-xl"
                    data-testid="admin-edit-role-select"
                  >
                    <SelectValue
                      placeholder={
                        profile.role_name
                          ? `Current: ${profile.role_name}`
                          : "Select role"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(roles.length ? roles : FALLBACK_ROLES.map((r) => ({ id: r, role_name: r }))).map(
                      (r) => (
                        <SelectItem key={r.id} value={r.id}>
                          <span className="capitalize">{r.role_name}</span>
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500">
                  Changing role updates this user&apos;s permissions across OreVital.
                </p>
              </div>

              {/* Ambassador (upline dealer) */}
              <div className="space-y-1.5" data-testid="admin-edit-ambassador">
                <Label className="text-xs flex items-center gap-1.5">
                  <UserRoundCheck size={13} className="text-violet-600" />
                  Ambassador (upline dealer)
                </Label>
                {ambSel ? (
                  <div
                    className="flex items-center justify-between gap-2 rounded-xl border border-violet-200 bg-violet-50/60 px-3 py-2"
                    data-testid="admin-edit-ambassador-current"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-violet-900 truncate">
                        {ambSel.name}
                      </div>
                      {ambSel.email && (
                        <div className="text-[11px] text-violet-700 truncate">{ambSel.email}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAmbSel(null);
                        setAmbQuery("");
                        setAmbResults([]);
                      }}
                      className="text-[11px] font-semibold text-rose-600 hover:underline shrink-0"
                      data-testid="admin-edit-ambassador-clear"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                    />
                    <Input
                      value={ambQuery}
                      onChange={(e) => setAmbQuery(e.target.value)}
                      placeholder="Search dealer by name or email…"
                      className="h-11 rounded-xl pl-9"
                      data-testid="admin-edit-ambassador-search"
                    />
                    {(ambSearching || ambResults.length > 0) && ambQuery.trim().length >= 2 && (
                      <div
                        className="absolute z-20 mt-1 w-full rounded-xl border border-zinc-200 bg-white shadow-lg max-h-60 overflow-auto"
                        data-testid="admin-edit-ambassador-results"
                      >
                        {ambSearching && (
                          <div className="px-3 py-2 text-xs text-zinc-500 flex items-center gap-2">
                            <Loader2 size={13} className="animate-spin" /> Searching…
                          </div>
                        )}
                        {!ambSearching && ambResults.length === 0 && (
                          <div className="px-3 py-2 text-xs text-zinc-500">No matches</div>
                        )}
                        {ambResults.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setAmbSel({
                                id: u.id,
                                name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "Dealer",
                                email: u.email || u.email_id || "",
                              });
                              setAmbResults([]);
                              setAmbQuery("");
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-violet-50 transition-colors"
                            data-testid={`admin-edit-ambassador-option-${u.id}`}
                          >
                            <div className="text-sm font-medium text-zinc-800 truncate">
                              {u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Dealer"}
                            </div>
                            <div className="text-[11px] text-zinc-500 truncate">{u.email || u.email_id}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-zinc-500">
                  Assign this customer to a dealer. Their existing &amp; future orders attach to that
                  dealer&apos;s commission ledger. Save to apply.
                </p>
              </div>

              {/* Commission */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <BadgePercent size={13} className="text-zinc-500" />
                  Commission %
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.5"
                  value={form.commission_perc}
                  onChange={(e) => update("commission_perc", e.target.value)}
                  className="h-11 rounded-xl"
                  data-testid="admin-edit-commission"
                />
              </div>

              {/* Tax exempt toggle */}
              <div className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-3.5">
                <div>
                  <div className="text-sm font-medium text-zinc-900">
                    Tax exempt
                  </div>
                  <div className="text-xs text-zinc-500">
                    Excludes sales tax on this user&apos;s orders.
                  </div>
                </div>
                <Switch
                  checked={!!form.tax_exempted}
                  onCheckedChange={(v) => update("tax_exempted", v ? 1 : 0)}
                  data-testid="admin-edit-tax"
                />
              </div>

              {/* Approved toggle */}
              <div className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-3.5">
                <div>
                  <div className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-emerald-500" />
                    Approved
                  </div>
                  <div className="text-xs text-zinc-500">
                    New sign-ups start unapproved (0). Toggle on to approve this dealer (1).
                  </div>
                </div>
                <Switch
                  checked={!!form.approved}
                  onCheckedChange={(v) => update("approved", v ? 1 : 0)}
                  data-testid="admin-edit-approved"
                />
              </div>

              {/* Contact details */}
              <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-3.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                  <Phone size={13} /> Contact
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[11px]">First name</Label>
                    <Input
                      value={form.first_name || ""}
                      onChange={(e) => update("first_name", e.target.value)}
                      className="h-10 rounded-lg"
                      data-testid="admin-edit-first-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">Last name</Label>
                    <Input
                      value={form.last_name || ""}
                      onChange={(e) => update("last_name", e.target.value)}
                      className="h-10 rounded-lg"
                      data-testid="admin-edit-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Phone</Label>
                  <Input
                    value={form.phone_no || ""}
                    onChange={(e) => update("phone_no", e.target.value)}
                    className="h-10 rounded-lg"
                    data-testid="admin-edit-phone"
                  />
                </div>
              </div>

              {/* Billing address */}
              <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-3.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                  <MapPin size={13} /> Billing address
                </div>
                <AddressFields prefix="" form={form} update={update} />
              </div>

              {/* Shipping address */}
              <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-3.5">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                    <Truck size={13} /> Shipping address
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-600">
                    Same as billing
                    <Switch
                      checked={!!form.ship_same_as_billing}
                      onCheckedChange={(v) =>
                        update("ship_same_as_billing", v ? 1 : 0)
                      }
                      data-testid="admin-edit-ship-same"
                    />
                  </label>
                </div>
                {form.ship_same_as_billing ? (
                  <p
                    className="text-[11px] text-zinc-500"
                    data-testid="admin-edit-ship-same-note"
                  >
                    Orders ship to the billing address above.
                  </p>
                ) : (
                  <AddressFields prefix="ship_" form={form} update={update} />
                )}
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="admin-edit-save"
              >
                {saving ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <Save size={16} className="mr-2" />
                )}
                Save changes
              </Button>
            </div>

            <Separator />

            {/* Reset password */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                <KeyRound size={13} />
                Reset password
              </h3>
              <p className="text-xs text-zinc-500">
                Sets a new password directly. The user&apos;s current password is
                not required.
              </p>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min 6 chars)"
                className="h-11 rounded-xl"
                data-testid="admin-edit-new-password"
              />
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={savingPwd || newPassword.length < 6}
                className="w-full h-11 rounded-xl border-zinc-300"
                data-testid="admin-edit-reset-password"
              >
                {savingPwd ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <KeyRound size={16} className="mr-2" />
                )}
                Set new password
              </Button>
            </div>

            <Separator />

            {/* Stats footer */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-zinc-50 p-2.5">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                  Commission
                </div>
                <div className="text-sm font-semibold text-zinc-900">
                  {profile.commission_perc || 0}%
                </div>
              </div>
              <div className="rounded-xl bg-zinc-50 p-2.5">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                  Tax exempt
                </div>
                <div className="text-sm font-semibold text-zinc-900">
                  {profile.tax_exempted ? "Yes" : "No"}
                </div>
              </div>
              <div className="rounded-xl bg-zinc-50 p-2.5">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                  Approved
                </div>
                <div className="text-sm font-semibold text-zinc-900 flex items-center justify-center gap-1">
                  <CircleDot
                    size={12}
                    className={
                      profile.approved
                        ? "text-emerald-500"
                        : "text-zinc-400"
                    }
                  />
                  {profile.approved ? "Active" : "Inactive"}
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>

    <OrderDetailSheet
      open={orderSheetOpen}
      orderRecordId={expandedOrderId}
      userRecordId={callerUserRecordId}
      isAdmin={true}
      onUpdated={() => {
        if (profile?.user_id) {
          fetchMyOrders(profile.user_id, 25)
            .then((r) => setRecentOrders(r?.orders || []))
            .catch(() => {});
        }
      }}
      onOpenChange={(v) => {
        setOrderSheetOpen(v);
        if (!v) setExpandedOrderId(null);
      }}
    />
    </>
  );
}


/**
 * Reusable billing/shipping address field group. `prefix` is "" for billing
 * and "ship_" for shipping, so the same inputs drive both Airtable field sets.
 */
function AddressFields({ prefix, form, update }) {
  const k = (name) => `${prefix}${name}`;
  return (
    <div className="space-y-2">
      <Input
        value={form[k("address")] || ""}
        onChange={(e) => update(k("address"), e.target.value)}
        placeholder="Street address"
        className="h-10 rounded-lg"
        data-testid={`admin-edit-${prefix || "bill_"}address`}
      />
      <Input
        value={form[k("address2")] || ""}
        onChange={(e) => update(k("address2"), e.target.value)}
        placeholder="Apt, suite, etc. (optional)"
        className="h-10 rounded-lg"
        data-testid={`admin-edit-${prefix || "bill_"}address2`}
      />
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={form[k("city")] || ""}
          onChange={(e) => update(k("city"), e.target.value)}
          placeholder="City"
          className="h-10 rounded-lg"
          data-testid={`admin-edit-${prefix || "bill_"}city`}
        />
        <Input
          value={form[k("state")] || ""}
          onChange={(e) => update(k("state"), e.target.value)}
          placeholder="State"
          className="h-10 rounded-lg"
          data-testid={`admin-edit-${prefix || "bill_"}state`}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={form[k("postcode")] || ""}
          onChange={(e) => update(k("postcode"), e.target.value)}
          placeholder="ZIP / Postcode"
          className="h-10 rounded-lg"
          data-testid={`admin-edit-${prefix || "bill_"}postcode`}
        />
        <select
          value={form[k("country")] || "US"}
          onChange={(e) => update(k("country"), e.target.value)}
          className="h-10 rounded-lg border border-zinc-200 px-2 text-sm bg-white w-full"
          data-testid={`admin-edit-${prefix || "bill_"}country`}
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </div>
    </div>
  );
}


/**
 * Admin-side shop handle (user_name) editor with live availability check.
 *
 * Mirrors the dealer-facing UsernameField in Profile.jsx but adapted for the
 * admin sheet's layout — the surrounding Section already provides the label.
 */
function AdminShopHandleField({ value, onChange, userRecordId }) {
  const [check, setCheck] = useState({
    status: "idle",
    available: null,
    message: "",
  });

  useEffect(() => {
    const v = (value || "").trim();
    if (v.length < 3) {
      setCheck({ status: "idle", available: null, message: "" });
      return undefined;
    }
    const t = setTimeout(async () => {
      setCheck((c) => ({ ...c, status: "checking" }));
      try {
        const res = await checkUsernameAvailable(v, userRecordId);
        setCheck({
          status: "done",
          available: res?.available,
          message: res?.available
            ? "Available"
            : res?.message || "Already taken",
        });
      } catch {
        setCheck({ status: "done", available: null, message: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value, userRecordId]);

  const Icon =
    check.status === "checking"
      ? Loader2
      : check.available === true
      ? Check
      : check.available === false
      ? X
      : AtSign;
  const tone =
    check.available === true
      ? "text-emerald-600"
      : check.available === false
      ? "text-rose-600"
      : "text-zinc-400";

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Shop handle (user_name)</Label>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) =>
            onChange(
              e.target.value.replace(/[^a-zA-Z0-9._-]/g, "").toLowerCase()
            )
          }
          placeholder="your-handle"
          className="h-11 rounded-xl pr-9"
          data-testid="admin-edit-shop-handle"
        />
        <Icon
          size={14}
          className={`absolute right-3 top-1/2 -translate-y-1/2 ${tone} ${
            check.status === "checking" ? "animate-spin" : ""
          }`}
        />
      </div>
      {value && (
        <div className="text-[11px] font-mono text-violet-700">
          postcue.com/d/{value}
        </div>
      )}
      {check.message && (
        <div className={`text-[11px] ${tone}`}>{check.message}</div>
      )}
    </div>
  );
}

